import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CreateExercise } from 'src/models/exercise.model';
import imageCompression from 'browser-image-compression';
import { IonModal } from '@ionic/angular';

@Component({
    selector: 'app-create-exercise',
    templateUrl: './create-exercise.component.html',
    styleUrls: ['./create-exercise.component.scss'],
    standalone: false
})
export class CreateExerciseComponent {
  @Input() sportName: string = '';
  @Input() description: string = '';
  selectedFile: File | null = null;
  @Input() imagePreview: string | ArrayBuffer | null = null;
  presentingElement: Element | null = null;
  @Output() emitCreatedExercse = new EventEmitter<CreateExercise>();
  @Input() confirmText = 'Add Exercise';
  @Input() loading = false;

  @Input() hashtags: string[] = [];
  toggleStates = new Map<string, boolean>();

  @Input() secondaryHashtags: string[] = [];
  secondaryToggleStates = new Map<string, boolean>();

  isProcessing = false;

  @ViewChild('gifRecorderModal') gifRecorderModal!: IonModal;
  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor() {}

  ionViewWillEnter() {
    this.presentingElement = document.querySelector('.ion-page');
  }

  emitExercise() {
    this.loading = true;
    this.emitCreatedExercse.emit({
      selectedFile: this.selectedFile,
      imagePreview: this.imagePreview,
      toggleStates: this.toggleStates,
      secondaryToggleStates: this.secondaryToggleStates,
      sportName: this.sportName,
      description: this.description,
    });
  }

  addGifFromUrl(url: string) {
    this.imagePreview = url;
    this.selectedFile = null;
  }

  async onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      await this.processFile(file);
    }
  }

  async processFile(file: File, skipCompression: boolean = false) {
    this.isProcessing = true;
    let processedFile: File | null = null;

    try {
      if (file.type === 'image/gif') {
        const maxSizeInBytes = 4 * 1024 * 1024; // 4MB limit
        if (file.size > maxSizeInBytes) {
          throw new Error(
            'GIF file is too large. Please choose a file smaller than 4MB.'
          );
        }
        processedFile = file; // No compression for GIFs, just use the original file
      } else if (skipCompression) {
        processedFile = file; // Skip compression if skipCompression is true
      } else {
        const options = {
          maxSizeMB: 0.1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedBlob = await imageCompression(file, options);
        processedFile = new File([compressedBlob], file.name, {
          type: compressedBlob.type,
          lastModified: Date.now(),
        });
      }

      if (processedFile) {
        this.selectedFile = processedFile;

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target?.result as string | ArrayBuffer | null;
        };
        reader.readAsDataURL(processedFile);
      } else {
        throw new Error('Failed to process file');
      }
    } catch (error) {
      alert('Error processing image: ' + (error as Error).message);
    } finally {
      this.isProcessing = false;
    }
  }

  deleteImage(event: Event) {
    event.stopPropagation(); // Prevent the click event from bubbling up to the parent
    this.selectedFile = null;
    this.imagePreview = '';
  }

  handleGifRecorded(gifUrl: string) {
    this.addGifFromUrl(gifUrl);
  }

  openGifRecorderModal(event: Event) {
    // Only open the modal if the click didn't originate from the delete icon
    if (!(event.target as HTMLElement).closest('.delete-icon')) {
      this.gifRecorderModal.present();
    }
  }

  onGifRecordingComplete(event: any) {
    this.processFile(event, true);
    this.gifRecorderModal.dismiss();
  }

  openFileInput() {
    this.fileInput.nativeElement.click();
  }
}
